// Building blocks shared by the four phone inboxes: swipeable rows, email cards, folder chips, the full-screen thread view and the bulk bar.
import { useRef, useState } from "react";
import { Check, CheckCircle2, ChevronLeft, ChevronRight, FolderInput, Loader2, MailOpen, Paperclip, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { BottomSheet, SheetRow, SectionTitle, TOPBAR_OFFSET } from "@/components/phone/PhoneBits";
import { fmtDate } from "@/pages/applications/inboxShared";
import { cn } from "@/lib/utils";

const THRESHOLD = 80;

/** Swipe right → `right.onTrigger()`, swipe left → `left.onTrigger()`; hold 450 ms → `onLongPress()`. A swipe never counts as a tap. */
export function SwipeRow({ children, right, left, onLongPress, disabled = false, testid }) {
  const [dx, setDx] = useState(0);
  const start = useRef(null);
  const timer = useRef(null);
  const moved = useRef(false);

  const onTouchStart = (e) => {
    if (disabled) return;
    const t = e.touches[0];
    start.current = { x: t.clientX, y: t.clientY, axis: null };
    moved.current = false;
    if (onLongPress) timer.current = setTimeout(() => { if (!moved.current) { moved.current = true; start.current = null; navigator.vibrate?.(10); onLongPress(); } }, 450);
  };
  const onTouchMove = (e) => {
    if (!start.current) return;
    const t = e.touches[0];
    const ddx = t.clientX - start.current.x, ddy = t.clientY - start.current.y;
    if (!start.current.axis) {
      if (Math.abs(ddx) < 6 && Math.abs(ddy) < 6) return;
      start.current.axis = Math.abs(ddx) > Math.abs(ddy) ? "x" : "y";
    }
    moved.current = true;
    clearTimeout(timer.current);
    if (start.current.axis !== "x") return;
    const c = Math.max(-130, Math.min(130, ddx));
    setDx((c > 0 && !right) || (c < 0 && !left) ? 0 : c);
  };
  const onTouchEnd = () => {
    clearTimeout(timer.current);
    const d = dx;
    setDx(0);
    start.current = null;
    if (d > THRESHOLD && right) right.onTrigger();
    else if (d < -THRESHOLD && left) left.onTrigger();
  };
  const onClickCapture = (e) => { if (moved.current) { e.stopPropagation(); e.preventDefault(); moved.current = false; } };
  const armed = Math.abs(dx) > THRESHOLD;

  return (
    <div className="relative overflow-hidden select-none" style={{ touchAction: "pan-y" }} data-testid={testid}>
      <div className="absolute inset-0 flex items-center justify-between text-white text-[12px] font-semibold">
        <div className={cn("h-full flex items-center pl-5 gap-2 w-1/2", right?.tone || "bg-emerald-600", dx <= 0 && "opacity-0")}>{right?.icon}{right?.label}{armed && dx > 0 && <Check className="w-4 h-4" />}</div>
        <div className={cn("h-full flex items-center justify-end pr-5 gap-2 w-1/2", left?.tone || "bg-sky-600", dx >= 0 && "opacity-0")}>{armed && dx < 0 && <Check className="w-4 h-4" />}{left?.label}{left?.icon}</div>
      </div>
      <div className="relative bg-white" style={{ transform: `translateX(${dx}px)`, transition: dx === 0 ? "transform .18s ease-out" : "none" }}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd} onClickCapture={onClickCapture}>
        {children}
      </div>
    </div>
  );
}

export function PhoneEmailCard({ email, badges, selectMode, checked, onOpen, onToggle }) {
  const id = email.internet_message_id || email.id;
  return (
    <div role="button" tabIndex={0} onClick={() => (selectMode ? onToggle(email.id) : onOpen(email.id))}
      onKeyDown={(e) => { if (e.key === "Enter") (selectMode ? onToggle(email.id) : onOpen(email.id)); }}
      className={cn("w-full text-left px-3 py-3 flex gap-3 items-start active:bg-slate-50 sf-tap", checked && "bg-amber-50", !email.is_read && "border-l-[3px] border-l-blue-600")}
      data-testid={`phone-email-${id}`}>
      {selectMode && <Checkbox checked={!!checked} className="h-5 w-5 mt-0.5 pointer-events-none" aria-label="Select email" data-testid={`phone-email-select-${id}`} />}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className={cn("text-[14px] truncate flex-1", !email.is_read ? "font-bold text-slate-900" : "font-medium text-slate-800")}>{email.from_name || email.from_email || "—"}</span>
          {email.has_attachments && <Paperclip className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
          <span className="text-[11px] text-slate-500 shrink-0 tabular-nums">{fmtDate(email.received)}</span>
        </div>
        <div className={cn("text-[13px] truncate mt-0.5", !email.is_read ? "font-semibold text-slate-900" : "text-slate-700")}>{email.subject || "(no subject)"}</div>
        {email.preview && <div className="text-[12px] text-slate-500 truncate">{email.preview}</div>}
        <div className="flex items-center gap-1 mt-1.5 flex-wrap">{badges}</div>
      </div>
      {!selectMode && <ChevronRight className="w-4 h-4 text-slate-300 shrink-0 mt-1" />}
    </div>
  );
}

/** Folder chips along the top: the fixed tabs (Inbox · Done · …) then every mailbox folder. */
export function PhoneFolderChips({ tabs, folders = [], active, onChange }) {
  const all = [...tabs.map((t) => ({ id: t.id, label: t.label, count: t.count, badge: t.badge })), ...folders.map((f) => ({ id: f.id, label: f.name, count: f.total }))];
  return (
    <div className="flex gap-1.5 overflow-x-auto sf-no-scrollbar px-3 py-2 border-b bg-white" data-testid="phone-inbox-folders">
      {all.map((t) => (
        <button key={t.id} type="button" onClick={() => onChange(t.id)}
          className={cn("shrink-0 inline-flex items-center gap-1 h-8 px-3 rounded-full border text-[12px] font-medium sf-tap",
            active === t.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200")}
          data-testid={`phone-inbox-folder-${t.id}`}>
          <span className="max-w-[160px] truncate">{t.label}</span>
          {t.count != null && <span className={cn("tabular-nums text-[11px]", active === t.id ? "text-slate-300" : "text-slate-400")}>{t.count}</span>}
          {t.badge > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">{t.badge}</span>}
        </button>
      ))}
    </div>
  );
}

/** Full-screen reader/composer surface above the tab bar. `bottomBar` is pinned; the body scrolls. */
export function PhoneThreadView({ title, subtitle, onBack, children, bottomBar, testid = "phone-thread" }) {
  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid={testid}>
      <div className="shrink-0 border-b bg-white/95 backdrop-blur sf-safe-top">
        <div className="h-12 flex items-center gap-1 pl-1 pr-3">
          <button type="button" onClick={onBack} className="h-10 w-10 flex items-center justify-center rounded-lg text-slate-700 sf-tap active:bg-slate-100" aria-label="Back" data-testid={`${testid}-back`}>
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[15px] leading-tight truncate" data-testid={`${testid}-title`}>{title}</div>
            {subtitle && <div className="text-[11px] text-slate-500 truncate">{subtitle}</div>}
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto sf-thread" style={{ WebkitOverflowScrolling: "touch" }}>{children}</div>
      {bottomBar && <div className="shrink-0 border-t bg-white/95 backdrop-blur sf-safe-bottom" data-testid={`${testid}-bar`}>{bottomBar}</div>}
    </div>
  );
}

export function ThreadBarButton({ icon: Icon, label, onClick, primary = false, disabled = false, testid }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className={cn("flex-1 h-14 flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium sf-tap disabled:opacity-40", primary ? "text-primary" : "text-slate-700 active:bg-slate-50")}
      data-testid={testid}>
      <Icon className="w-5 h-5" />{label}
    </button>
  );
}

/** Fixed-bottom bulk bar for select mode: Move to done · Move to… · Assign · Mark read, with sheets for the pickers. */
export function PhoneBulkBar({ count, api, me, ids, folders, currentFolder, onDone, onClear, dept = "" }) {
  const [busy, setBusy] = useState(null);
  const [sheet, setSheet] = useState(null);     // move | assign
  const [people, setPeople] = useState(null);

  const run = async (label, payload) => {
    setBusy(label);
    setSheet(null);
    try {
      const r = await api.bulk({ message_ids: ids, actor: me?.name || me?.email || "", ...payload });
      const bits = [`${r.done} of ${ids.length} ${label}`];
      if (r.logged) bits.push(`${r.logged} logged on customer files`);
      if (r.unlogged) bits.push(`${r.unlogged} had no file (moved anyway)`);
      if (r.failed?.length) bits.push(`${r.failed.length} failed`);
      (r.failed?.length ? toast.warning : toast.success)(bits.join(" · "));
      onDone?.(r);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(null); }
  };
  const openAssign = () => { setSheet("assign"); if (!people) api.assignees().then((r) => setPeople(r.people || [])).catch(() => setPeople([])); };
  const targets = (folders || []).filter((f) => f.id !== currentFolder && !(currentFolder === "inbox" && f.kind === "inbox") && !(currentFolder === "done" && f.kind === "done"));

  return (
    <>
      <div className="fixed inset-x-0 z-40 bg-amber-50 border-t border-amber-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]" style={{ bottom: "calc(4rem + env(safe-area-inset-bottom, 0px))" }} data-testid="phone-bulk-bar">
        <div className="flex items-center gap-1 px-2 h-16">
          <span className="text-[12px] font-semibold text-amber-900 tabular-nums px-2 shrink-0" data-testid="phone-bulk-count">{count} selected</span>
          <div className="flex-1 flex items-center justify-around">
            {[
              { k: "done", icon: CheckCircle2, label: "Done", onClick: () => run("moved to Done", { action: "move_done" }), testid: "phone-bulk-done" },
              targets.length > 0 && { k: "move", icon: FolderInput, label: "Move to…", onClick: () => setSheet("move"), testid: "phone-bulk-move" },
              { k: "assign", icon: UserPlus, label: "Assign", onClick: openAssign, testid: "phone-bulk-assign" },
              { k: "read", icon: MailOpen, label: "Read", onClick: () => run("marked read", { action: "mark_read" }), testid: "phone-bulk-read" },
            ].filter(Boolean).map((b) => (
              <button key={b.k} type="button" onClick={b.onClick} disabled={!!busy} className="flex flex-col items-center gap-0.5 text-[11px] font-medium text-amber-900 px-2 sf-tap disabled:opacity-50" data-testid={b.testid}>
                {busy && b.k === "done" && busy.startsWith("moved to Done") ? <Loader2 className="w-5 h-5 animate-spin" /> : <b.icon className="w-5 h-5" />}{b.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={onClear} className="h-10 w-10 flex items-center justify-center rounded-full text-amber-900 sf-tap" aria-label="Clear selection" data-testid="phone-bulk-clear"><X className="w-5 h-5" /></button>
        </div>
      </div>
      <BottomSheet open={sheet === "move"} onClose={() => setSheet(null)} title={`Move ${count} to…`} testid="phone-bulk-move-sheet">
        <div className="-mx-1">{targets.map((f) => <SheetRow key={f.id} icon={FolderInput} label={f.name} sub={f.total != null ? `${f.total} emails` : undefined} onClick={() => run(`moved to ${f.name}`, { action: "move", folder_id: f.kind === "inbox" ? "inbox" : f.kind === "done" ? "done" : f.id })} testid={`phone-bulk-move-${f.kind === "folder" ? f.id : f.kind}`} />)}</div>
      </BottomSheet>
      <BottomSheet open={sheet === "assign"} onClose={() => setSheet(null)} title={`Assign ${count} to…`} testid="phone-bulk-assign-sheet">
        <div className="-mx-1">
          <SheetRow icon={UserPlus} label="Assign to me" sub={me?.email} onClick={() => run("assigned to you", { action: "assign", assignee: me?.name || me?.email || "Me", assignee_email: me?.email || "" })} testid="phone-bulk-assign-me" />
          {people === null && <div className="px-3 py-3 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading people…</div>}
          {[true, false].map((team) => {
            const rows = (people || []).filter((p) => p.name !== (me?.name || "") && !!p.team === team);
            if (!rows.length) return null;
            return (
              <div key={String(team)} className="mt-2">
                <SectionTitle>{team ? (dept === "legal" ? "Legal team" : dept === "applications" ? "Applications team" : "Collections team") : "Everyone else"}</SectionTitle>
                {rows.map((p) => <SheetRow key={p.email || p.name} label={p.name} sub={p.email} onClick={() => run(`assigned to ${p.name}`, { action: "assign", assignee: p.name, assignee_email: p.email })} testid={`phone-bulk-assign-${(p.name || "").replace(/\s+/g, "-").toLowerCase()}`} />)}
              </div>
            );
          })}
          <SheetRow label="Unassign" tone="text-rose-700" onClick={() => run("unassigned", { action: "assign", assignee: "" })} testid="phone-bulk-unassign" />
        </div>
      </BottomSheet>
    </>
  );
}

/** Sticky-under-topbar inbox header: title line + the tool buttons on the right. */
export function PhoneInboxHeader({ title, mailbox, count, children }) {
  return (
    <div className="sticky z-30 bg-white border-b" style={{ top: TOPBAR_OFFSET }} data-testid="phone-inbox-header">
      <div className="flex items-center gap-2 px-3 h-12">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[15px] leading-tight truncate" data-testid="phone-inbox-title">{title}</div>
          <div className="text-[11px] text-slate-500 truncate font-mono" data-testid="phone-inbox-mailbox">{mailbox || "…"}{count != null ? ` · ${count}` : ""}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">{children}</div>
      </div>
    </div>
  );
}
